// DynamicPage.tsx

import React from 'react';
import { Link } from 'react-router-dom';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

import Container from '../components/container';
import DividerBlock from '../components/divider';
import Iframe from '../components/iframe';
import Ingress from '../components/ingress';
import Carousel from '../components/carousel';
import Hero from '../components/hero';
import ImageGallery from '../components/image-gallery';
import PageHeader from '../components/page-header';
import SingleImage from '../components/single-image';
import TextBox from '../components/text-box';
import TitleParagraph from '../components/title-paragraph';
import { useConfig } from '../ConfigContext';
import { cleanComponentData } from '../utils/component-utils';
import CircularProgress from '@mui/material/CircularProgress';

// Define the component type
interface PageComponent {
  type: string;
  component: React.FC<any>; // Use a more specific type for props if possible
}

// Create the public exported array of components
export const availableComponents: PageComponent[] = [
  { type: 'Container', component: Container },
  { type: 'Hero', component: Hero },
  { type: 'PageHeader', component: PageHeader },
  { type: 'Ingress', component: Ingress },
  { type: 'TitleParagraph', component: TitleParagraph },
  { type: 'Carousel', component: Carousel },
  { type: 'TextBox', component: TextBox },
  { type: 'ImageGallery', component: ImageGallery },
  { type: 'SingleImage', component: SingleImage },
  { type: 'Iframe', component: Iframe },
  { type: 'Divider', component: DividerBlock },
];
interface DynamicPageProps {
  pageName: string;
  parentPath?: string;
}

const DynamicPage: React.FC<DynamicPageProps> = ({ pageName, parentPath }) => {
  const { config } = useConfig();
  const [pageImagesLoaded, setPageImagesLoaded] = React.useState(false);

  // Get the page configuration based on pageName and parentPath
  const getPageConfig = () => {
    if (!config || !config.pages) {
      console.error('Config or pages not found');
      return null;
    }

    // Special handling for page keys that might contain slashes
    // Check if we're dealing with a path like "UX/UI/suvux" where "UX/UI" is a single page key
    if (parentPath) {
      // First, check if the parentPath is a direct key in the pages object
      // This handles cases where the page key contains slashes
      if (
        config.pages[parentPath] &&
        config.pages[parentPath].subpages?.[pageName]
      ) {
        // Use non-null assertion since we've already checked that these properties exist
        return config.pages[parentPath].subpages![pageName];
      }

      // If not found as a direct key, try the standard path traversal approach
      try {
        // For subpages, we need to handle the parent path correctly
        // Split the path, but be careful with potential slashes in page keys
        const allAvailableRootPages = Object.keys(config.pages);

        // Find the correct root page, which might include slashes
        const rootPage = allAvailableRootPages.find(
          (pageKey) =>
            parentPath === pageKey || parentPath.startsWith(`${pageKey}/`),
        );

        if (!rootPage) {
          console.error(`No matching root page found for path ${parentPath}`);

          return null;
        }

        let currentLevel = config.pages[rootPage];

        // Process the rest of the path if there's anything after the root page
        if (
          parentPath.length > rootPage.length &&
          parentPath.charAt(rootPage.length) === '/'
        ) {
          const remainingPath = parentPath.substring(rootPage.length + 1); // +1 to skip the slash
          const pathParts = remainingPath.split('/');

          // Navigate through the rest of the path
          for (const part of pathParts) {
            if (!currentLevel.subpages || !currentLevel.subpages[part]) {
              console.error(`Subpage ${part} not found in path ${parentPath}`);
              return null;
            }
            currentLevel = currentLevel.subpages[part];
          }
        }

        // Now look for the target page in the subpages
        if (currentLevel.subpages && currentLevel.subpages[pageName]) {
          return currentLevel.subpages[pageName];
        } else {
          console.error(`Subpage ${pageName} not found under ${parentPath}`);

          return null;
        }
      } catch (error) {
        console.error('Error finding subpage:', error);
        return null;
      }
    }

    // If no parent path or if no match found above, this is a top-level page
    return config.pages[pageName];
  };

  const pageConfig = getPageConfig();

  // Preload current page images when page changes
  React.useEffect(() => {
    const currentPageConfig = getPageConfig();
    if (currentPageConfig) {
      // Simple image extraction for current page only
      const imageUrls = new Set<string>();

      const getImageUrl = (image: string | any): string => {
        return typeof image === 'string' ? image : image?.url || '';
      };

      const extractImagesFromComponents = (components: any[]) => {
        components.forEach((component) => {
          const props = component.props || {};
          const styles = component.styles || {};

          if (props.image) imageUrls.add(getImageUrl(props.image));
          if (props.images?.length) {
            props.images.forEach((img: any) => imageUrls.add(getImageUrl(img)));
          }
          if (styles.backgroundImage)
            imageUrls.add(getImageUrl(styles.backgroundImage));

          if (props.container?.items) {
            extractImagesFromComponents(props.container.items);
          }
          if (props.container?.tabs) {
            props.container.tabs.forEach((tab: any) => {
              if (tab.items) {
                extractImagesFromComponents(tab.items);
              }
            });
          }
        });
      };

      if (currentPageConfig.components) {
        extractImagesFromComponents(currentPageConfig.components);
      }

      // Preload page-specific images
      const preloadPromises = Array.from(imageUrls).map((url) => {
        if (!url) return Promise.resolve();
        return new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Continue on error
          img.src = url;
        });
      });

      Promise.all(preloadPromises).finally(() => {
        setPageImagesLoaded(true);
      });
    } else {
      setPageImagesLoaded(true);
    }
  }, [pageName, parentPath, config]);

  // Inject SEO meta tags on page change
  React.useEffect(() => {
    if (!pageConfig) return;
    const seo = pageConfig.seo;
    const hostname = window.location.hostname;

    document.title = seo?.title || pageConfig.menutitle || hostname;

    const setNameMeta = (name: string, content: string) => {
      let el = document.querySelector(
        `meta[name="${name}"]`,
      ) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setPropMeta = (property: string, content: string) => {
      let el = document.querySelector(
        `meta[property="${property}"]`,
      ) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setNameMeta('robots', seo?.noIndex ? 'noindex,nofollow' : 'index,follow');

    if (seo?.description) setNameMeta('description', seo.description);
    if (seo?.keywords) setNameMeta('keywords', seo.keywords);
    if (seo?.aiSummary) setNameMeta('ai-summary', seo.aiSummary);

    setPropMeta('og:title', seo?.title || pageConfig.menutitle || hostname);
    if (seo?.description) setPropMeta('og:description', seo.description);
    if (seo?.ogImage) setPropMeta('og:image', seo.ogImage);
  }, [pageName, parentPath, pageConfig]);

  if (!pageConfig) {
    console.error(
      `Page not found! Page name: ${pageName}, Parent path: ${parentPath}`,
    );
    return (
      <Box sx={{ padding: 4 }}>
        <h2>Page Not Found</h2>
        <p>The requested page "{pageName}" could not be found.</p>
        <p>Path: {parentPath ? `${parentPath}/${pageName}` : pageName}</p>
        <Button component={Link} to="/" variant="contained" color="primary">
          Return to Home
        </Button>
      </Box>
    );
  }

  // Show loading for page-specific images
  if (!pageImagesLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Helper function to render a single component
  const renderComponent = (componentData: any, index: number) => {
    const cleanedComponent = cleanComponentData(componentData);
    const component = availableComponents.find(
      (c) => c.type === cleanedComponent.type,
    );
    const backgroundUrl = cleanedComponent.styles?.backgroundImage?.url;

    if (component) {
      // Special handling for Container component
      if (component.type === 'Container' && cleanedComponent.props?.container) {
        return (
          <Box
            key={index}
            sx={{
              width: '100%',
              ...(cleanedComponent.styles as SxProps<Theme>),
              background: backgroundUrl
                ? `url(${backgroundUrl})`
                : cleanedComponent.styles?.background,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              justifyContent: 'center', // Center horizontally
              boxSizing: 'border-box',
              margin: '0 auto', // Center the container
              px: 2, // Add some horizontal padding
            }}
          >
            <Box
              sx={{
                width: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Container
                id={cleanedComponent.id} // Pass the id
                columns={cleanedComponent.props.container.columns || 2}
                spacing={cleanedComponent.props.container.spacing || 2}
                items={cleanedComponent.props.container.items || []}
                maxWidth={cleanedComponent.props.container.maxWidth || 1100}
                minColumns={cleanedComponent.props.container.minColumns}
                useTabs={cleanedComponent.props.container.useTabs || false}
                tabs={cleanedComponent.props.container.tabs || []}
                renderComponent={renderComponent}
                sx={cleanedComponent.styles}
              />
            </Box>
          </Box>
        );
      }
      return (
        <Box
          key={index}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            ...(cleanedComponent.styles as SxProps<Theme>),
            background: backgroundUrl
              ? `url(${backgroundUrl})`
              : cleanedComponent.styles?.background,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            boxSizing: 'border-box',
          }}
        >
          {React.createElement(component.component, {
            ...cleanedComponent.props,
            id: cleanedComponent.id, // Pass the id to the component
            sx: cleanedComponent.styles, // Pass styles as sx prop
          })}
        </Box>
      );
    } else {
      return null;
    }
  };

  return (
    <Box
      sx={{
        overflow: 'hidden',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        justifyContent: 'center',
        paddingLeft: 0,
        paddingRight: 0,
      }}
    >
      {pageConfig.components.map((componentData, index) =>
        renderComponent(componentData, index),
      )}
    </Box>
  );
};

export default DynamicPage;
